// ═══════════════════════════════════════════════════════════════════════════
// PAGE ADMIN COMITÉS — fragments de rendu purs
// Extrait de page-admin-committees.ts (`renderCommitteesPage` à 242 lignes, à
// 8 du cliquet taille-fonction, 2026-09-27) : tri des comités et table de la
// page, PURS — l'état de tri (mutable dans la page) et le droit d'écriture
// sont passés au moment du rendu. Comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t.

import type { CommitteeEntity } from './page-admin-committees.js';

export type CommitteeSortKey = 'code' | 'name' | 'position' | 'actions_count';

/** Copie triée des comités selon la colonne et le sens demandés. */
export function sortCommittees(
  arr: ReadonlyArray<CommitteeEntity>,
  sortKey: CommitteeSortKey,
  sortDir: 'asc' | 'desc',
): CommitteeEntity[] {
  return [...arr].sort((a, b) => {
    let va: string | number;
    let vb: string | number;
    switch (sortKey) {
      case 'code':
        va = a.code;
        vb = b.code;
        break;
      case 'name':
        va = a.name.toLowerCase();
        vb = b.name.toLowerCase();
        break;
      case 'actions_count':
        va = a.actions_count || 0;
        vb = b.actions_count || 0;
        break;
      default:
        va = a.position || 0;
        vb = b.position || 0;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
}

function th(
  key: CommitteeSortKey,
  label: string,
  sortKey: CommitteeSortKey,
  sortDir: 'asc' | 'desc',
): string {
  const active = sortKey === key;
  const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
  return `<th class="sortable${active ? ' sorted' : ''}" data-sort-comm="${key}" style="cursor:pointer;user-select:none">${esc(label)} <span style="font-size:10px">${arrow}</span></th>`;
}

/**
 * Carte `#committees-card` : aide au glisser-déposer, en-têtes triables et
 * lignes des comités.
 *
 * `canWrite` conditionne tout ce qui écrit : la poignée de réordonnancement
 * (active seulement en tri par position croissante) et les boutons ✎ / ✕. Le
 * clic sur la ligne (`data-click-comm`) navigue vers la fiche — une lecture,
 * offerte à tout le monde (revue front §I3).
 */
export function buildCommitteesTableHtml(
  committees: ReadonlyArray<CommitteeEntity>,
  sortKey: CommitteeSortKey,
  sortDir: 'asc' | 'desc',
  canWrite: boolean,
): string {
  const sorted = sortCommittees(committees, sortKey, sortDir);
  // Le réordonnancement est une écriture : il suit `committees.write`, en plus
  // de la condition de tri qui le rendait déjà possible ou non.
  const dragEnabled = sortKey === 'position' && sortDir === 'asc' && canWrite;
  const dragTitle = dragEnabled
    ? t('committees.drag.title_enabled')
    : t('committees.drag.title_disabled');
  const dragAria = dragEnabled
    ? t('stages.dragdrop.aria_handle')
    : t('committees.drag.aria_disabled');
  const rows = sorted
    .map(
      (
        c,
      ) => `<tr class="committee-drag-row" ${dragEnabled ? 'draggable="true"' : ''} data-committee-id="${c.id}" style="${dragEnabled ? 'cursor:grab' : ''}">
      <td style="width:32px;text-align:center" title="${esc(dragTitle)}"><span class="drag-handle" role="button" aria-label="${esc(dragAria)}" ${dragEnabled ? '' : 'aria-disabled="true"'}>⠿</span></td>
      <td style="cursor:pointer" data-click-comm="${c.id}"><span style="font-family:var(--font-mono);font-size:12px;font-weight:600;text-decoration:underline">${esc(c.code)}</span></td>
      <td style="cursor:pointer" data-click-comm="${c.id}">${esc(c.name)}</td>
      <td style="font-size:12px;color:var(--text3);text-align:center">${c.position || 0}</td>
      <td style="font-size:12px;text-align:center">${c.actions_count || 0}</td>
      <td style="white-space:nowrap">${
        // Le crayon menait aussi à la fiche, mais promettait une édition que la
        // fiche refusera — on le retire avec la corbeille (revue front §I3).
        canWrite
          ? `<button class="btn btn-secondary btn-icon" data-edit-comm="${c.id}" title="${esc(t('common.edit'))}">✎</button>
        <button class="btn btn-danger btn-icon" data-del-comm="${c.id}" data-comm-name="${esc(c.name)}" data-comm-used="${c.in_use ? '1' : '0'}" style="margin-left:4px" title="${esc(t('common.delete'))}">✕</button>`
          : ''
      }</td>
    </tr>`,
    )
    .join('');
  return `<div class="card" id="committees-card">
      <p style="font-size:12px;color:var(--text3);margin:0 0 10px 0">${t('committees.dragdrop.help')}</p>
      <div class="table-wrap"><table>
        <thead><tr><th style="width:32px"></th>${th('code', t('committees.col.code'), sortKey, sortDir)}${th('name', t('committees.col.label'), sortKey, sortDir)}${th('position', t('committees.col.position'), sortKey, sortDir)}${th('actions_count', t('committees.col.actions_count', { plural: t('vocabulary.action_plural') }), sortKey, sortDir)}<th></th></tr></thead>
        <tbody id="committees-tbody">${rows || `<tr><td colspan="6" class="empty">${esc(t('committees.empty'))}</td></tr>`}</tbody>
      </table></div></div>`;
}
